import { useEffect, useState } from "react";
import { useContent } from "@/lib/useContent";

// Scotland projection bounds (matches /map page)
const BOUNDS = { latMin: 54.5, latMax: 60.9, lngMin: -8.8, lngMax: -1.4 };
const W = 600, H = 800;

function project(lat, lng) {
    const x = ((lng - BOUNDS.lngMin) / (BOUNDS.lngMax - BOUNDS.lngMin)) * W;
    const y = H - ((lat - BOUNDS.latMin) / (BOUNDS.latMax - BOUNDS.latMin)) * H;
    return { x, y };
}

const DEFAULT_COPY = {
    overline: "Field Notes from Above",
    title_line_1: "The Flight Path",
    title_line_2: "of a Survivor.",
    intro: "Every pin is somewhere I've stood with my drone.",
    pins: [],
};

export default function VisitedMap() {
    const { data: copy } = useContent("visited_locations", DEFAULT_COPY);
    const pins = (copy.pins || []).map((v) => ({ ...v, ...project(v.lat, v.lng) }));
    const [active, setActive] = useState(null);

    useEffect(() => {
        if (pins.length && !active) setActive(pins[0].id);
    }, [pins.length, active]);

    if (!pins.length) return null;

    const activePin = pins.find((p) => p.id === active) || pins[0];
    const pathD = pins.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");

    return (
        <section className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid="visited-map-section">
            <div className="mb-12 max-w-3xl">
                <div className="overline mb-3">{copy.overline}</div>
                <h2 className="font-serif text-4xl lg:text-6xl tracking-tighter leading-[0.95]">
                    {copy.title_line_1}<br/>
                    <span className="italic text-[var(--sb-brand)]">{copy.title_line_2}</span>
                </h2>
                <p className="text-[var(--sb-text-dim)] mt-6 text-lg leading-relaxed">
                    {copy.intro}
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                <div className="lg:col-span-7 card overflow-hidden" data-testid="visited-map">
                    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
                        <defs>
                            <radialGradient id="sea2" cx="50%" cy="50%" r="80%">
                                <stop offset="0%" stopColor="#142a4d" />
                                <stop offset="100%" stopColor="#0b1f3a" />
                            </radialGradient>
                            <linearGradient id="land2" x1="0" x2="0" y1="0" y2="1">
                                <stop offset="0%" stopColor="#2a322a" />
                                <stop offset="100%" stopColor="#1a201a" />
                            </linearGradient>
                            <filter id="glow">
                                <feGaussianBlur stdDeviation="4" result="b"/>
                                <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
                            </filter>
                        </defs>
                        <rect width={W} height={H} fill="url(#sea2)" />

                        <g fill="url(#land2)" stroke="#3a463a" strokeWidth="1.2">
                            <path d="M 220 120
                                     C 250 80, 320 60, 360 110
                                     C 410 100, 430 160, 410 200
                                     C 460 215, 460 270, 410 290
                                     C 430 330, 400 380, 360 400
                                     C 380 440, 360 490, 320 510
                                     C 340 560, 310 610, 270 620
                                     C 280 670, 240 720, 200 700
                                     C 170 740, 120 720, 130 670
                                     C 90 660, 70 610, 110 580
                                     C 80 540, 100 490, 140 480
                                     C 110 430, 140 380, 180 380
                                     C 150 330, 180 270, 220 270
                                     C 200 230, 200 170, 220 120 Z" />
                            <path d="M 90 320 C 110 300, 130 320, 120 350 C 130 380, 100 390, 80 370 Z" />
                            <path d="M 60 410 C 80 395, 95 415, 85 440 C 100 460, 70 470, 55 450 Z" />
                            <path d="M 110 250 C 130 235, 145 260, 130 280 Z" />
                            <path d="M 310 60 C 325 50, 340 60, 330 75 Z" />
                            <path d="M 350 50 C 365 45, 375 55, 365 68 Z" />
                            <path d="M 420 30 C 430 22, 440 30, 432 42 Z" />
                        </g>

                        <path d={pathD} fill="none" stroke="#6ba7de" strokeWidth="1.5" strokeDasharray="4 4" opacity="0.6" />

                        {pins.map((p) => {
                            const isActive = p.id === active;
                            const anchor = p.label_anchor === "right" ? "end" : "start";
                            const dx = p.label_anchor === "right" ? -12 : 12;
                            return (
                                <g key={p.id} transform={`translate(${p.x}, ${p.y})`} className="cursor-pointer" onClick={() => setActive(p.id)} data-testid={`visited-pin-${p.id}`}>
                                    <circle r={isActive ? 16 : 12} fill="#6ba7de" opacity={isActive ? "0.35" : "0.14"} filter={isActive ? "url(#glow)" : undefined} />
                                    <circle r={isActive ? 7 : 5} fill="#6ba7de" stroke="#ffffff" strokeWidth="1.2" />
                                    <text
                                        x={dx} y="4"
                                        fontSize="14"
                                        textAnchor={anchor}
                                        fill={isActive ? "#ffffff" : "#b8c8de"}
                                        style={{fontFamily: "Cormorant Garamond, serif", letterSpacing: "0.02em", paintOrder: "stroke", stroke: "#0b1f3a", strokeWidth: "3px", strokeLinejoin: "round"}}
                                    >
                                        {p.name}
                                    </text>
                                </g>
                            );
                        })}
                    </svg>
                </div>

                <div className="lg:col-span-5 lg:sticky lg:top-24 space-y-4">
                    {activePin && (
                        <div className="card p-8" data-testid="visited-detail">
                            <div className="overline mb-3">Stop {pins.findIndex((p) => p.id === active) + 1} of {pins.length}</div>
                            <h3 className="font-serif text-4xl tracking-tighter leading-none mb-3">{activePin.name}</h3>
                            <p className="text-[var(--sb-text-dim)] text-base leading-relaxed">{activePin.note}</p>
                            <div className="mt-5 text-xs text-[var(--sb-text-muted)] uppercase tracking-[0.2em]">
                                {activePin.lat.toFixed(3)}° N · {Math.abs(activePin.lng).toFixed(3)}° W
                            </div>
                        </div>
                    )}
                    <div className="card p-4" data-testid="visited-list">
                        <div className="overline mb-3 px-2">All stops, in order</div>
                        <ol className="space-y-1">
                            {pins.map((p, i) => (
                                <li key={p.id}>
                                    <button
                                        onClick={() => setActive(p.id)}
                                        className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-3 transition-colors ${
                                            active === p.id ? "text-[var(--sb-text)] bg-[var(--sb-bg)]" : "text-[var(--sb-text-dim)] hover:text-[var(--sb-text)]"
                                        }`}
                                        data-testid={`visited-list-${p.id}`}
                                    >
                                        <span className="flex items-center gap-3">
                                            <span className="font-mono text-[var(--sb-text-muted)] text-xs">{String(i + 1).padStart(2, "0")}</span>
                                            <span>{p.name}</span>
                                        </span>
                                        {active === p.id && <span className="text-[var(--sb-brand)] text-xs uppercase tracking-wider">Viewing</span>}
                                    </button>
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>
            </div>
        </section>
    );
}
